// Full-document render of the tempurpedic OfferIndexPage: PageSkeleton (the
// React document shell) wrapping the offer-index layout (normal site chrome).
//
// Load-bearing: the `body` StreamField renders with every top-level block
// wrapped in `<div class="w-block-{type} block-{type}">`, reproduced per
// top-level block via the per-block `StreamField tagName="div"` render (the
// LegalPage house pattern). Each of the 5 OfferContent block types dispatches
// to its existing block component.
import { Immutable } from "immer";
import React from "react";

import { templates } from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { EverydayOfferGridBlock } from "../components/EverydayOfferGridBlock";
import { HeroImageBlock } from "../components/HeroImageBlock";
import { OfferBannerBlock } from "../components/OfferBannerBlock";
import { PageSkeleton } from "../components/PageSkeleton";
import { Template as LimitedTimeOfferGridBlock } from "./LimitedTimeOfferGridBlockTemplate";
import { Template as ProductCalloutBlock } from "./ProductCalloutBlockTemplate";

// Mutable reactivated props (not Immutable<>): this template composes the
// in-tree SiteHeader chain via PageSkeleton, which is typed with mutable
// reactivated props (see PageSkeleton).
type IProps = templates.OfferIndexPageTemplate;

type IBodyBlock = NonNullable<IProps["page"]["body"]>["value"][number];
type BlockValue<T extends IBodyBlock["type"]> = Extract<
    IBodyBlock,
    { type: T }
>["value"];

export const offerIndexComponentsMap = {
    limited_time_grid: (block: {
        value: Immutable<BlockValue<"limited_time_grid">>;
    }) => <LimitedTimeOfferGridBlock value={block.value} />,
    everyday_grid: (block: {
        value: Immutable<BlockValue<"everyday_grid">>;
    }) => <EverydayOfferGridBlock value={block.value} />,
    banner: (block: { value: Immutable<BlockValue<"banner">> }) => (
        <OfferBannerBlock value={block.value} />
    ),
    hero: (block: { value: Immutable<BlockValue<"hero">> }) => (
        <HeroImageBlock value={block.value} />
    ),
    product_callout_block: (block: {
        value: Immutable<BlockValue<"product_callout_block">>;
    }) => <ProductCalloutBlock value={block.value} />,
};

export const Template = (props: IProps): React.JSX.Element => {
    // `body` is nullable (`null=True` on the model field).
    const body = props.page.body;
    return (
        <PageSkeleton pageChrome={props.page_chrome}>
            <div className="offer-index-page u-full-width-title">
                {body?.value.map((block) => (
                    <StreamField
                        key={block.id}
                        tagName="div"
                        attrs={{
                            className: `w-block-${block.type} block-${block.type}`,
                        }}
                        value={[block]}
                        components={offerIndexComponentsMap}
                    />
                ))}
            </div>
        </PageSkeleton>
    );
};
